import { useState } from 'react';
import {
  Plus, Search, LayoutGrid, List, ChevronDown, ChevronRight,
  Bot, FolderOpen, Shield,
} from 'lucide-react';
import type { Agent } from '../hooks/useAgents';
import type { Instance } from '../types/instance';
import { AgentCardGrid, AgentCardList } from './AgentCard';
import AgentForm, { type AgentFormData } from './AgentForm';
import { useCreateAgent, useUpdateAgent, useDeleteAgent, useDismissAgent } from '../hooks/useAgents';
import { useAgentGroups } from '../hooks/useAgentGroups';

type ViewMode = 'grid' | 'list';

interface AgentGalleryProps {
  projectId: string;
  agents: Agent[] | undefined;
  instances?: Instance[];
}

export default function AgentGallery({ projectId, agents, instances }: AgentGalleryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);

  const createAgent = useCreateAgent(projectId);
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const dismissAgent = useDismissAgent();
  const { data: groups } = useAgentGroups(projectId);

  // --- Separate system and user agents ---

  const allAgents = agents ?? [];
  const systemAgents = allAgents.filter(a => a.isSystem);
  const userAgents = allAgents.filter(a => !a.isSystem);

  // --- Filtering (applies to user agents) ---

  const filtered = userAgents.filter(a => {
    if (searchQuery && !a.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (roleFilter !== 'all' && a.role !== roleFilter) return false;
    if (groupFilter === 'ungrouped' && a.groupId) return false;
    if (groupFilter !== 'all' && groupFilter !== 'ungrouped' && a.groupId !== groupFilter) return false;
    return true;
  });

  // Also filter system agents by search query and role
  const filteredSystem = systemAgents.filter(a => {
    if (searchQuery && !a.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (roleFilter !== 'all' && a.role !== roleFilter) return false;
    return true;
  });

  // --- Grouping ---

  const groupedAgents = new Map<string | null, Agent[]>();
  for (const agent of filtered) {
    const key = agent.groupId || null;
    if (!groupedAgents.has(key)) groupedAgents.set(key, []);
    groupedAgents.get(key)!.push(agent);
  }

  const sortedGroupKeys: (string | null)[] = [];
  // Named groups first (sorted by group name)
  if (groups) {
    for (const g of groups) {
      if (groupedAgents.has(g.id)) sortedGroupKeys.push(g.id);
    }
  }
  // Ungrouped last
  if (groupedAgents.has(null)) sortedGroupKeys.push(null);

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // --- Actions ---

  const handleSubmit = async (data: AgentFormData) => {
    if (editingAgent) {
      await updateAgent.mutateAsync({ id: editingAgent.id, ...data });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await createAgent.mutateAsync(data as any);
    }
  };

  const handleClone = async (agent: Agent) => {
    await createAgent.mutateAsync({
      name: `${agent.name} (copy)`,
      role: agent.role,
      systemPrompt: agent.systemPrompt,
    });
  };

  const handleDelete = (agent: Agent) => {
    if (!confirm(`Delete agent "${agent.name}"?`)) return;
    deleteAgent.mutate(agent.id);
  };

  const handleDismiss = (agent: Agent) => {
    dismissAgent.mutate(agent.id);
  };

  const getGroupName = (groupId: string | null) => {
    if (!groupId) return 'Ungrouped';
    const group = groups?.find(g => g.id === groupId);
    return group?.name ?? 'Unknown Group';
  };

  const renderCard = (agent: Agent) => {
    const props = {
      agent,
      instances,
      onEdit: (a: Agent) => { setEditingAgent(a); setFormOpen(true); },
      onClone: handleClone,
      onDelete: handleDelete,
      onDismiss: handleDismiss,
    };
    return viewMode === 'grid'
      ? <AgentCardGrid key={agent.id} {...props} />
      : <AgentCardList key={agent.id} {...props} />;
  };

  // Determine if we should show grouped layout
  const hasGroups = sortedGroupKeys.some(k => k !== null);
  const isSystemCollapsed = collapsedGroups.has('__system__');

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1" style={{ minWidth: '200px' }}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
          />
        </div>

        {/* Role filter */}
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-slate-500 focus:outline-none"
        >
          <option value="all">All roles</option>
          <option value="creator">Creator</option>
          <option value="executor">Executor</option>
          <option value="researcher">Researcher</option>
        </select>

        {/* Group filter */}
        {groups && groups.length > 0 && (
          <select
            value={groupFilter}
            onChange={e => setGroupFilter(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-slate-500 focus:outline-none"
          >
            <option value="all">All groups</option>
            <option value="ungrouped">Ungrouped</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        )}

        {/* View toggle */}
        <div className="flex rounded-lg border border-slate-300">
          <button
            onClick={() => setViewMode('grid')}
            className={`rounded-l-lg p-2 ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-600'}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`rounded-r-lg p-2 ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-600'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>

        {/* Agent count */}
        <span className="text-sm text-slate-400">
          {filtered.length + filteredSystem.length} agent{filtered.length + filteredSystem.length !== 1 ? 's' : ''}
        </span>

        {/* New Agent */}
        <button
          onClick={() => { setEditingAgent(null); setFormOpen(true); }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          New Agent
        </button>
      </div>

      {/* Content */}
      {!allAgents.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <Bot className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">No agents yet.</p>
          <button
            onClick={() => { setEditingAgent(null); setFormOpen(true); }}
            className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Create First Agent
          </button>
        </div>
      ) : filtered.length === 0 && filteredSystem.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No agents match your filters</div>
      ) : (
        <div className="space-y-4">
          {/* System Agents Section */}
          {filteredSystem.length > 0 && (
            <div className="rounded-lg border border-slate-200">
              <button
                onClick={() => toggleGroup('__system__')}
                className="flex w-full items-center gap-2 px-4 py-3 text-left"
              >
                {isSystemCollapsed
                  ? <ChevronRight className="h-4 w-4 text-slate-400" />
                  : <ChevronDown className="h-4 w-4 text-slate-400" />
                }
                <Shield className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">System Agents</span>
                <span className="text-xs text-slate-400">({filteredSystem.length})</span>
              </button>
              {!isSystemCollapsed && (
                <div className={`p-4 pt-0 ${
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
                    : 'space-y-2'
                }`}>
                  {filteredSystem.map(renderCard)}
                </div>
              )}
            </div>
          )}

          {/* User Agents */}
          {hasGroups && groupFilter === 'all' ? (
            // Grouped layout
            sortedGroupKeys.map(key => {
              const groupAgents = groupedAgents.get(key) ?? [];
              if (groupAgents.length === 0) return null;
              const groupName = getGroupName(key);
              const isCollapsed = key ? collapsedGroups.has(key) : false;

              return (
                <div key={key ?? 'ungrouped'} className="rounded-lg border border-slate-200">
                  <button
                    onClick={() => key && toggleGroup(key)}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left"
                  >
                    {key ? (
                      isCollapsed
                        ? <ChevronRight className="h-4 w-4 text-slate-400" />
                        : <ChevronDown className="h-4 w-4 text-slate-400" />
                    ) : (
                      <FolderOpen className="h-4 w-4 text-slate-400" />
                    )}
                    <span className="text-sm font-medium text-slate-700">{groupName}</span>
                    <span className="text-xs text-slate-400">({groupAgents.length})</span>
                  </button>
                  {!isCollapsed && (
                    <div className={`p-4 pt-0 ${
                      viewMode === 'grid'
                        ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
                        : 'space-y-2'
                    }`}>
                      {groupAgents.map(renderCard)}
                    </div>
                  )}
                </div>
              );
            })
          ) : filtered.length > 0 ? (
            // Flat layout
            <div className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
                : 'space-y-2'
            }>
              {filtered.map(renderCard)}
            </div>
          ) : null}
        </div>
      )}

      {/* Agent Form Modal */}
      <AgentForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingAgent(null); }}
        onSubmit={handleSubmit}
        agent={editingAgent}
        groups={groups}
        isPending={createAgent.isPending || updateAgent.isPending}
      />
    </div>
  );
}
